import { useAuthStore } from "@features/auth/store/authStore";
import TestPassage from "@features/tests/components/TestPassage";
import TextRenderer from "@features/tests/components/TextRenderer";
import Button, { ButtonVariant } from "@shared/components/ui/Button";
import { isAnswerCorrect } from "@shared/utils/answerValidation";
import theme from "@shared/utils/themeColors";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import DifficultyBadge from "../../question-bank/components/DifficultyBadge";
import VideoPlayer from "../components/VideoPlayer";
import {
  useTutorialDetail,
  useTutorialProgress,
} from "../hooks/useTutorialBank";
import { TutorialBankTestAnswer } from "../types/tutorial-bank.types";

function TutorialBankDetail() {
  const { categoryId, tutorialId } = useParams<{
    categoryId: string;
    tutorialId: string;
  }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Manage question state
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  // Get tutorial details
  const {
    tutorial,
    loading: tutorialLoading,
    error: tutorialError,
  } = useTutorialDetail(tutorialId || "", categoryId || "");

  // Get/update tutorial progress
  const {
    progress,
    startSession,
    saveAnswer,
    completeTutorial,
    isStartingSession,
    isSavingAnswer,
  } = useTutorialProgress(tutorialId || "", tutorial?.categoryId || "");

  // Track if session has been started to prevent infinite API calls
  const sessionStartedRef = useRef<boolean>(false);

  // Start or update session when tutorial is loaded (only once)
  useEffect(() => {
    if (tutorial && user && !isStartingSession && !sessionStartedRef.current) {
      sessionStartedRef.current = true;
      startSession();
    }
  }, [tutorial, user, isStartingSession, startSession]);

  // Handle option selection
  const handleOptionSelect = (option: string) => {
    if (!isAnswerSubmitted) {
      setSelectedAnswer(option);
    }
  };

  // Check answer
  const handleSubmitAnswer = () => {
    console.log("selectedAnswer", selectedAnswer);
    console.log("tutorial", tutorial);
    console.log("progress", progress);
    if (!selectedAnswer || !tutorial?.question) return;

    const isCorrect = isAnswerCorrect(
      selectedAnswer,
      tutorial.question.correctAnswer
    );
    setIsCorrect(isCorrect);
    setIsAnswerSubmitted(true);

    // Save answer to backend
    const answer: TutorialBankTestAnswer = {
      questionId: tutorial.question.id || tutorial.question.questionBankItemId,
      selectedOption: selectedAnswer,
      answeredAt: Date.now(),
      isCorrect,
    };

    if (!progress) return;

    saveAnswer({ progressId: progress.id, answer });
  };

  const { nextTutorial, isLoadingNextTutorial: isLoadingNext } =
    useTutorialProgress(tutorialId, tutorial?.categoryId);

  // Move to next tutorial
  const handleContinue = () => {
    if (nextTutorial) {
      setSelectedAnswer(null);
      setIsAnswerSubmitted(false);
      setIsCorrect(null);
      navigate(`/tutorial-bank/tutorial/${categoryId}/${nextTutorial.id}`);
    } else {
      if (progress) {
        completeTutorial(progress.id);
      } else {
        toast.success("Tutorial completed!");
      }
      navigate(`/tutorial-bank/category/${tutorial?.categoryId}`);
    }
  };

  // Restart the tutorial
  const handleRestart = () => {
    setSelectedAnswer(null);
    setIsAnswerSubmitted(false);
    setIsCorrect(null);

    // Only try to manipulate the video if it's a direct video (not an iframe)
    const isDirectVideo =
      tutorial?.video &&
      (!tutorial.video.sourceType ||
        tutorial.video.sourceType === "UPLOADED" ||
        tutorial.video.sourceType === "EXTERNAL");

    if (isDirectVideo && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  // Loading or error states
  const isLoading = tutorialLoading;
  const error = tutorialError;

  return (
    <AuthenticatedLayout
      isLoading={isLoading}
      error={error}
      pageHeader={{
        title: tutorial?.header?.title || "Tutorial",
        actions: [
          {
            label: "Back to Category",
            onClick: () => navigate(`/tutorial-bank/category/${categoryId}`),
          },
        ],
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="grid gap-8">
          {/* Tutorial metadata */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {tutorial?.header?.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {tutorial?.header?.subtitle}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {tutorial?.difficulty && (
                  <DifficultyBadge difficulty={tutorial.difficulty} />
                )}
              </div>
            </div>

            {/* Tutorial video section - centered at the top */}
            {tutorial?.video ? (
              <div className="w-full max-w-4xl mx-auto mb-8">
                <div className="aspect-video rounded-lg overflow-hidden bg-black">
                  <VideoPlayer ref={videoRef} video={tutorial.video} />
                </div>
                <h3 className="text-lg font-semibold mt-4 mb-2">
                  {tutorial.video.title}
                </h3>
                {tutorial.video.description && (
                  <TextRenderer
                    text={tutorial.video.description}
                    allowedAttr={["class"]}
                  />
                )}
              </div>
            ) : (
              <div className="w-full max-w-4xl mx-auto mb-8 flex items-center justify-center aspect-video bg-gray-100 dark:bg-gray-900 rounded-lg">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-gray-600 dark:text-gray-400">
                    This tutorial does not have a video available
                  </p>
                </div>
              </div>
            )}

            {tutorial?.directions && (
              <div className="mb-6">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
                  Directions
                </h3>
                <div className="prose dark:prose-invert max-w-none">
                  <TextRenderer
                    text={tutorial.directions}
                    allowedAttr={["class"]}
                  />
                </div>
              </div>
            )}

            {/* Tutorial content */}
            {tutorial?.introduction?.title && (
              <div className="mb-6">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
                  {tutorial.introduction.title}
                </h3>
                {tutorial.introduction.description && (
                  <div className="prose dark:prose-invert max-w-none">
                    <TextRenderer
                      text={tutorial.introduction.description}
                      allowedAttr={["class"]}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {tutorial?.passages &&
            tutorial.passages.map((passage: any) => (
              <TestPassage
                key={`${passage.id}-passage`}
                passage={passage}
                highlightedLines={{}}
                highlightedQuestionNumber={undefined}
                renderText={true}
              />
            ))}

          {/* Question section */}
          {tutorial?.question && (
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4">Practice Question</h3>

              <div className="mb-6">
                <div className="prose dark:prose-invert max-w-none">
                  <TextRenderer
                    text={tutorial.question.text}
                    allowedAttr={["class"]}
                  />
                </div>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-6">
                {Object.entries(tutorial.question.options).length > 0 ? (
                  Object.entries(tutorial.question.options).map(
                    ([option, text]) => (
                      <div
                        key={option}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          selectedAnswer === option
                            ? isAnswerSubmitted
                              ? isCorrect
                                ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                                : selectedAnswer ===
                                  tutorial.question?.correctAnswer
                                ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                                : "bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700"
                              : "bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700"
                            : isAnswerSubmitted &&
                              option === tutorial.question.correctAnswer
                            ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                            : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        }`}
                        onClick={() => handleOptionSelect(option)}
                      >
                        <div className="flex items-start">
                          <div
                            className={`flex-shrink-0 h-5 w-5 rounded-full border flex items-center justify-center mr-3 mt-0.5 ${
                              selectedAnswer === option
                                ? "border-indigo-500 bg-indigo-500"
                                : "border-gray-400"
                            }`}
                          >
                            {selectedAnswer === option && (
                              <div className="h-2 w-2 rounded-full bg-white"></div>
                            )}
                          </div>
                          <div className="flex-grow">
                            <span
                              className={`font-medium ${
                                isAnswerSubmitted &&
                                ((selectedAnswer === option && !isCorrect) ||
                                  option === tutorial.question.correctAnswer)
                                  ? option === tutorial.question.correctAnswer
                                    ? "text-green-700 dark:text-green-300"
                                    : "text-red-700 dark:text-red-300"
                                  : "text-gray-800 dark:text-gray-200"
                              }`}
                            >
                              <div className="flex-1 text-left flex">
                                <span className="font-medium mr-2">
                                  {option}.
                                </span>
                                {text && (
                                  <div>
                                    <TextRenderer
                                      text={text}
                                      allowHtml={true}
                                      allowedTags={[
                                        "sup",
                                        "sub",
                                        "b",
                                        "i",
                                        "em",
                                        "strong",
                                        "span",
                                      ]}
                                      allowedAttr={["class"]}
                                    />
                                  </div>
                                )}
                              </div>
                            </span>
                          </div>

                          {/* Correct/incorrect icons */}
                          {isAnswerSubmitted && (
                            <div className="ml-3 flex-shrink-0">
                              {option === tutorial.question.correctAnswer ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 text-green-500 dark:text-green-400"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : selectedAnswer === option ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 text-red-500 dark:text-red-400"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )
                ) : (
                  // Free text question
                  <div className="w-full">
                    <div className="relative">
                      <textarea
                        value={selectedAnswer || ""}
                        onChange={(e) =>
                          !isAnswerSubmitted &&
                          setSelectedAnswer(e.target.value)
                        }
                        placeholder="Write your answer here..."
                        className={`w-full min-h-[100px] p-3  ${
                          isAnswerSubmitted
                            ? isCorrect
                              ? "border-green-300 bg-green-50 dark:bg-green-900/20"
                              : "border-red-300 bg-red-50 dark:bg-red-900/20"
                            : `border-gray-200 dark:border-gray-700 ${theme.border.brand} `
                        } rounded-lg resize-y`}
                        disabled={isAnswerSubmitted}
                      />
                      {isAnswerSubmitted && (
                        <div className="absolute top-2 right-2">
                          {isCorrect ? (
                            <div className="flex items-center bg-green-100 dark:bg-green-800/40 text-green-800 dark:text-green-200 px-2 py-1 rounded-md">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-1"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="font-medium">Correct</span>
                            </div>
                          ) : (
                            <div className="flex items-center bg-red-100 dark:bg-red-800/40 text-red-800 dark:text-red-200 px-2 py-1 rounded-md">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-1"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="font-medium">Incorrect</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {isAnswerSubmitted &&
                      !isCorrect &&
                      tutorial.question.correctAnswer && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-center text-blue-800 dark:text-blue-200 mb-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                            </svg>
                            <span className="font-medium">Correct answer:</span>
                          </div>
                          <div className="text-gray-800 dark:text-gray-200">
                            <TextRenderer
                              text={tutorial.question.correctAnswer}
                              allowHtml={true}
                              allowedTags={[
                                "sup",
                                "sub",
                                "b",
                                "i",
                                "em",
                                "strong",
                                "span",
                              ]}
                              allowedAttr={["class"]}
                            />
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>

              {/* Explanation - shown after answering */}
              {isAnswerSubmitted && tutorial.question.explanation && (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    Explanation
                  </h4>
                  <div className="prose dark:prose-invert max-w-none">
                    <TextRenderer
                      text={tutorial.question.explanation}
                      allowedAttr={["class"]}
                    />
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-6 flex flex-wrap justify-end gap-3">
                {isAnswerSubmitted ? (
                  <>
                    <Button
                      onClick={handleRestart}
                      variant={ButtonVariant.TERTIARY}
                    >
                      Retry
                    </Button>
                    <Button
                      onClick={handleContinue}
                      variant={ButtonVariant.PRIMARY}
                      disabled={isLoadingNext}
                    >
                      {isLoadingNext ? (
                        "Loading..."
                      ) : nextTutorial ? (
                        <>
                          Next Tutorial <span className="ml-1">→</span>
                        </>
                      ) : (
                        "Complete & Return"
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleSubmitAnswer}
                    disabled={!selectedAnswer || isSavingAnswer}
                    variant={ButtonVariant.PRIMARY}
                  >
                    {isSavingAnswer ? "Saving..." : "Check Answer"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  );
}

export default TutorialBankDetail;
